import { useEffect, useRef, useState } from "react";
import { salonInfo } from "@/mocks/content";

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [bgScale, setBgScale] = useState(1);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -rect.top / rect.height));
      setBgScale(1 + progress * 0.2);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 md:py-40 overflow-hidden bg-[#FAFAF8]"
    >
      {/* Typo Explosion Background */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        style={{ transform: `scale(${bgScale})`, transition: "transform 0.1s linear" }}
      >
        <span
          className="text-[#E8E6E1] font-serif font-bold whitespace-nowrap"
          style={{
            fontSize: "clamp(8rem, 20vw, 22rem)",
            letterSpacing: "-0.02em",
            opacity: 0.5,
            fontFamily: "'Noto Serif JP', serif",
          }}
        >
          RELAX
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          {/* Left: Image */}
          <div
            className="md:col-span-4"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.8s ease, transform 0.8s ease",
            }}
          >
            <div className="relative">
              <div className="w-full aspect-[3/4] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=elegant%20mens%20spa%20interior%20corridor%20with%20warm%20wooden%20panels%2C%20soft%20indirect%20lighting%2C%20minimalist%20japanese%20design%2C%20clean%20lines%2C%20serene%20atmosphere%2C%20warm%20beige%20and%20charcoal%20tones%2C%20luxury%20wellness%20center%2C%20no%20people&width=600&height=800&seq=about1&orientation=portrait"
                  alt="サロン内観"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Accent line */}
              <div
                className="absolute -bottom-4 -right-4 w-24 h-24 border border-[#8B7355]"
                style={{
                  opacity: visible ? 1 : 0,
                  transition: "opacity 0.6s ease 0.6s",
                }}
              />
            </div>
          </div>

          {/* Right: Text */}
          <div className="md:col-span-8">
            <p
              className="text-[#8B7355] text-xs tracking-[0.4em] mb-4 font-light"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                opacity: visible ? 1 : 0,
                transition: "opacity 0.6s ease 0.4s",
              }}
            >
              ABOUT US
            </p>

            <h2
              className="text-[#2C2C2C] font-light mb-8 leading-tight"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease 0.5s, transform 0.7s ease 0.5s",
              }}
            >
              男性のための、<br />
              専門的なボディケア
            </h2>

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[#4A4A4A] text-sm leading-[1.9] font-light"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease 0.7s, transform 0.7s ease 0.7s",
              }}
            >
              <p>
                {salonInfo.description}
              </p>
              <p>
                施術はすべて完全個室にて行われます。プライバシーを大切にしながら、お客様が心からリラックスできる環境をご用意しております。
              </p>
            </div>

            {/* Stats */}
            <div
              className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-[#E8E6E1]"
              style={{
                opacity: visible ? 1 : 0,
                transition: "opacity 0.7s ease 0.9s",
              }}
            >
              {[
                { num: "2018", label: "創業年" },
                { num: `${salonInfo.seats}`, label: "完全個室" },
                { num: "5+", label: "専門コース" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p
                    className="text-[#8B7355] font-light mb-1"
                    style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}
                  >
                    {stat.num}
                  </p>
                  <p className="text-[#4A4A4A] text-xs tracking-[0.2em]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
